/* ═══════════════════════════════════════════════════════
   INNOVA SD35 landing section — behaviour

   Replaces what webflow.js and the Flowbase boosters did on
   the original Webflow page:
     · image ticker under the hero
     · background videos: play while on screen, pause / play buttons
     · phone video slider (arrows, numbered dots, swipe)
     · FAQ accordion

   No dependencies. Every query is scoped to the section's
   .ivsd35 wrapper, and it re-initialises when the theme
   editor re-renders the section.
   ═══════════════════════════════════════════════════════ */
(function () {
  'use strict';

  var SLIDE_MS = 500; // matches the CSS transitions in innova-sd35.css

  function toArray(list) {
    return Array.prototype.slice.call(list);
  }

  /* ── Image ticker ─────────────────────────────────────
     The cards are rendered once. Copies are appended until the strip still
     covers the box after one full set has scrolled away, then the strip
     slides left by exactly one set, forever. */
  function initTicker(box) {
    var track = box.firstElementChild;
    if (!track || !track.children.length) return;
    var originals = toArray(track.children);

    function fill() {
      var first = originals[0].getBoundingClientRect();
      var last = originals[originals.length - 1].getBoundingClientRect();
      var gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      var setWidth = last.right - first.left + gap;
      if (!(setWidth > 0)) return;

      var setsNeeded = Math.ceil(box.offsetWidth / setWidth) + 1;
      var setsPresent = track.children.length / originals.length;
      for (; setsPresent < setsNeeded; setsPresent++) {
        originals.forEach(function (card) {
          var copy = card.cloneNode(true);
          copy.setAttribute('aria-hidden', 'true');
          copy.removeAttribute('data-shopify-editor-block');
          track.appendChild(copy);
        });
      }
      track.style.setProperty('--sd35-ticker-shift', -setWidth + 'px');
      track.classList.add('sd35-is-running');
    }

    fill();
    // Card width changes at the 991px breakpoint, so the loop length does too.
    var timer;
    window.addEventListener('resize', function () {
      clearTimeout(timer);
      timer = setTimeout(fill, 150);
    });
  }

  /* ── Background videos ────────────────────────────────
     A video plays only while it is on screen: it starts when it scrolls into
     view and pauses when it leaves. That is what Chrome and Safari already do
     with a muted `autoplay` video, and what the Webflow page relied on; doing
     it here makes every browser behave the same. It matters most for the
     closing clip, which plays once — it must not run out before anyone has
     scrolled down to it. */
  function initVideo(box) {
    var video = box.querySelector('video');
    var button = box.querySelector('[data-sd35-video-toggle]');
    if (!video) return;
    var pausedByVisitor = false;
    var seen = false;

    function play() {
      var attempt = video.play();
      if (attempt && attempt.catch) attempt.catch(function () {});
    }

    if ('IntersectionObserver' in window) {
      // Take over from the `autoplay` attribute straight away, so nothing off
      // screen is left running until the observer's first report arrives.
      var rect = box.getBoundingClientRect();
      var onScreen = rect.width > 0 && rect.height > 0 &&
        rect.bottom > 0 && rect.top < window.innerHeight &&
        rect.right > 0 && rect.left < window.innerWidth;
      if (!onScreen) {
        video.pause();
        if (!video.loop && video.currentTime > 0) video.currentTime = 0;
      }

      // "On screen" means at least 1% of the box is showing. The observer
      // reports each time that line is crossed. Plain "is it intersecting?" is
      // not enough: a slide parked beside the visible one touches the slider's
      // edge and counts as intersecting with nothing showing, and a page
      // loaded in a hidden pane reports zero-size boxes as fully visible.
      var VISIBLE = 0.01;
      new IntersectionObserver(function (entries) {
        var entry = entries[entries.length - 1];
        var area = entry.intersectionRect.width * entry.intersectionRect.height;
        if (entry.isIntersecting && entry.intersectionRatio >= VISIBLE && area > 0) {
          seen = true;
          // A clip that has played through stays on its last frame.
          if (video.paused && !video.ended && !pausedByVisitor) play();
        } else {
          if (!video.paused) video.pause();
          // Browsers that start `autoplay` videos off screen: rewind a
          // play-once clip so its first showing starts from the beginning.
          if (!seen && !video.loop && video.currentTime > 0) video.currentTime = 0;
        }
      }, { threshold: [0, VISIBLE] }).observe(box);
    }
    if (!button) return;

    var pauseIcon = button.children[0];
    var playIcon = button.children[1];
    function sync() {
      pauseIcon.hidden = video.paused;
      playIcon.hidden = !video.paused;
    }
    button.addEventListener('click', function () {
      pausedByVisitor = !video.paused;
      if (video.paused) play();
      else video.pause();
    });
    video.addEventListener('play', sync);
    video.addEventListener('pause', sync);
  }

  /* ── Phone video slider ───────────────────────────────
     Slides sit side by side; each is shifted by whole slide-widths
     (translateX in %), so nothing needs re-measuring on resize. Going past
     either end wraps round in the same direction. */
  function initSlider(slider) {
    var mask = slider.querySelector('.sd35-w-slider-mask');
    var nav = slider.querySelector('.sd35-w-slider-nav');
    var prev = slider.querySelector('.sd35-w-slider-arrow-left');
    var next = slider.querySelector('.sd35-w-slider-arrow-right');
    if (!mask) return;
    var slides = toArray(mask.children);
    var count = slides.length;
    var index = 0;
    var busy = false;
    var dots = [];

    function rest(i) {
      return slides.map(function () { return -i; });
    }

    function place(offsets, animate) {
      slider.classList.toggle('sd35-is-snapping', !animate);
      slides.forEach(function (slide, i) {
        slide.style.transform = 'translateX(' + offsets[i] * 100 + '%)';
      });
      if (!animate) void mask.offsetWidth; // commit the jump before the next transition
    }

    function mark() {
      slides.forEach(function (slide, i) {
        if (i === index) slide.removeAttribute('aria-hidden');
        else slide.setAttribute('aria-hidden', 'true');
      });
      dots.forEach(function (dot, i) {
        var active = i === index;
        dot.classList.toggle('sd35-w-active', active);
        dot.setAttribute('aria-pressed', active ? 'true' : 'false');
        dot.tabIndex = active ? 0 : -1;
      });
    }

    function go(to) {
      if (busy || count < 2) return;
      var from = index;
      var wrapForward = from === count - 1 && to === count;
      var wrapBack = from === 0 && to === -1;
      to = (to + count) % count;
      if (to === from) return;
      busy = true;

      var end = rest(to);
      var settle = null;
      if (wrapForward || wrapBack) {
        var step = wrapForward ? -1 : 1;
        var start = rest(from);
        // Park the incoming slide just off-screen on the side it enters from.
        start[to] = wrapForward ? 1 : -count;
        place(start, false);
        end = start.map(function (offset) { return offset + step; });
        settle = rest(to);
      }

      index = to;
      mark();
      place(end, true);
      setTimeout(function () {
        if (settle) place(settle, false);
        busy = false;
      }, SLIDE_MS + 20);
    }

    function onActivate(el, fn) {
      if (!el) return;
      el.addEventListener('click', fn);
      el.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          fn();
        }
      });
    }

    if (nav) {
      nav.textContent = '';
      dots = slides.map(function (slide, i) {
        var dot = document.createElement('div');
        dot.className = 'sd35-w-slider-dot';
        dot.textContent = i + 1;
        dot.setAttribute('role', 'button');
        dot.setAttribute('aria-label', 'Show slide ' + (i + 1) + ' of ' + count);
        onActivate(dot, function () { go(i); });
        nav.appendChild(dot);
        return dot;
      });
    }
    onActivate(prev, function () { go(index - 1); });
    onActivate(next, function () { go(index + 1); });

    slider.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowLeft') go(index - 1);
      else if (e.key === 'ArrowRight') go(index + 1);
    });

    var startX = null;
    var startY = null;
    slider.addEventListener('touchstart', function (e) {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
    }, { passive: true });
    slider.addEventListener('touchend', function (e) {
      if (startX === null) return;
      var dx = e.changedTouches[0].clientX - startX;
      var dy = e.changedTouches[0].clientY - startY;
      startX = null;
      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.5) go(index + (dx < 0 ? 1 : -1));
    }, { passive: true });

    place(rest(0), false);
    mark();
  }

  /* ── FAQ accordion ────────────────────────────────────
     One answer open at a time; a click anywhere else closes it, as the
     Webflow dropdown it replaces did. */
  function faqParts(item) {
    return {
      toggle: item.querySelector('.sd35-faq-accordian-toggle'),
      list: item.querySelector('.sd35-faq-accordian-list')
    };
  }

  function setFaqState(item, open) {
    var parts = faqParts(item);
    item.classList.toggle('sd35-is-open', open);
    parts.toggle.classList.toggle('sd35-w--open', open);
    parts.list.classList.toggle('sd35-w--open', open);
    parts.toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
  }

  function openFaq(item) {
    if (item.classList.contains('sd35-is-open')) return;
    var list = faqParts(item).list;
    // Measure the open height with transitions off (scrollHeight would count
    // the answer's 10px starting offset), then animate to it from zero.
    list.style.transition = 'none';
    list.style.height = 'auto';
    var target = list.offsetHeight;
    list.style.height = '';
    void list.offsetHeight;
    list.style.transition = '';
    setFaqState(item, true);
    list.style.height = target + 'px';
    // Once open, let the answer size itself (images load, the window resizes).
    setTimeout(function () {
      if (item.classList.contains('sd35-is-open')) list.style.height = 'auto';
    }, SLIDE_MS + 20);
  }

  function closeFaq(item) {
    if (!item.classList.contains('sd35-is-open')) return;
    var list = faqParts(item).list;
    list.style.height = list.offsetHeight + 'px';
    void list.offsetHeight; // start the transition from a real pixel height
    setFaqState(item, false);
    list.style.height = '';
  }

  function initFaq(root) {
    var items = toArray(root.querySelectorAll('[data-sd35-faq]'));
    items.forEach(function (item) {
      var toggle = faqParts(item).toggle;
      toggle.addEventListener('click', function () {
        var wasOpen = item.classList.contains('sd35-is-open');
        items.forEach(closeFaq);
        if (!wasOpen) openFaq(item);
      });
      toggle.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          toggle.click();
        } else if (e.key === 'Escape') {
          closeFaq(item);
        }
      });
    });
  }

  document.addEventListener('click', function (e) {
    toArray(document.querySelectorAll('[data-sd35-faq].sd35-is-open')).forEach(function (item) {
      if (!item.contains(e.target)) closeFaq(item);
    });
  });

  /* ── Boot ─────────────────────────────────────────── */
  function init(root) {
    if (!root || root.dataset.sd35Ready === '1') return;
    root.dataset.sd35Ready = '1';
    document.documentElement.classList.add('sd35-js');

    toArray(root.querySelectorAll('[data-sd35-ticker]')).forEach(initTicker);
    toArray(root.querySelectorAll('[data-sd35-video]')).forEach(initVideo);
    toArray(root.querySelectorAll('[data-sd35-slider]')).forEach(initSlider);
    initFaq(root);
  }

  function boot() {
    toArray(document.querySelectorAll('[data-sd35]')).forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Theme editor: the section's markup is replaced when a setting changes.
  document.addEventListener('shopify:section:load', function (e) {
    toArray(e.target.querySelectorAll('[data-sd35]')).forEach(init);
  });

  // Theme editor: selecting an FAQ block in the sidebar opens that answer.
  document.addEventListener('shopify:block:select', function (e) {
    if (e.target.matches && e.target.matches('[data-sd35-faq]')) {
      toArray(e.target.parentNode.querySelectorAll('[data-sd35-faq]')).forEach(closeFaq);
      openFaq(e.target);
    }
  });
})();
